import React, { useCallback, useRef } from 'react';
import { Box, TableCell } from '@mui/material';
import { ICandidateGroup } from './types';
import PoolList from '../CandidateSourcing/PoolList';
import SourceIconLink from '../Components/CustomUIElements/SourceIconLink';

function CandidateGroupTableRows({
  column,
  item,
  onViewPool,
  expandedPools,
  onTogglePoolsExpansion,
  onHoverCandidate,
  onUnhoverCandidate
}: {
  column: any;
  item: ICandidateGroup;
  onViewPool?: (poolId: number) => void;
  expandedPools?: Set<number>;
  onTogglePoolsExpansion?: (candidateGroupId: number) => void;
  onHoverCandidate?: (target: HTMLElement, candidateId: number) => void;
  onUnhoverCandidate?: () => void;
}) {
  const showAllPools = expandedPools?.has(item.id) || false;
  const nameCellRef = useRef<HTMLTableCellElement>(null);

  const handleMouseEnter = () => {
    if (nameCellRef.current) {
      onHoverCandidate?.(nameCellRef.current, item.id);
    }
  };

  const handleMouseLeave = () => {
    onUnhoverCandidate?.();
  };

  const Link = useCallback(
    ({ content }: { content: string }) => (
      <a
        href={`/admin/candidate_sourcing/candidate_groups/${item.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    ),
    [item.id]
  );

  switch (column.id) {
    case 'name':
      return (
        <TableCell
          ref={nameCellRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ cursor: 'pointer' }}
        >
          <Link content={item.name} />
        </TableCell>
      );
    case 'position':
      return <TableCell>{item.position}</TableCell>;
    case 'location':
      return <TableCell>{item.location}</TableCell>;
    case 'sources':
      return (
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
            {item.candidates.map((candidate) => (
              <SourceIconLink key={candidate.url} source={candidate.source} url={candidate.url} />
            ))}
          </Box>
        </TableCell>
      );
    case 'pools':
      return (
        <TableCell sx={{ maxWidth: '20vw' }}>
          <PoolList
            candidateGroup={item}
            onViewPool={onViewPool}
            showAllPools={showAllPools}
            onTogglePoolsExpansion={onTogglePoolsExpansion}
            showItemsThreshold={5}
          />
        </TableCell>
      );
    default:
      return <TableCell></TableCell>;
  }
}

export const candidateGroupTableColumn = [
  { id: 'name', label: 'Name', enabled: true, sortable: true, canToggle: false },
  { id: 'position', label: 'Position', enabled: true, sortable: false, canToggle: false },
  { id: 'location', label: 'Location', enabled: true, sortable: false, canToggle: false },
  { id: 'sources', label: 'Sources', enabled: true, sortable: false, canToggle: false },
  { id: 'pools', label: 'Talent Pools', enabled: true, sortable: false, canToggle: false }
];

export default React.memo(CandidateGroupTableRows, (prevProps, nextProps) => {
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.name === nextProps.item.name &&
    prevProps.item.position === nextProps.item.position &&
    prevProps.item.location === nextProps.item.location &&
    JSON.stringify(prevProps.item.candidates) === JSON.stringify(nextProps.item.candidates) &&
    JSON.stringify(prevProps.item.candidate_pools) === JSON.stringify(nextProps.item.candidate_pools) &&
    prevProps.expandedPools?.has(prevProps.item.id) === nextProps.expandedPools?.has(nextProps.item.id)
  );
});
